import { ProjectTask, TaskType } from "../../../interfaces/ProjectTask";

/** Filtro temporal da aba Board: tudo (sem filtro), semana ISO ou mês-calendário. */
export type BoardPeriod = "all" | "week" | "month";

const DAY_MS = 24 * 60 * 60 * 1000;

export const isoMonday = (d: Date): Date => {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = (date.getDay() + 6) % 7;
  return new Date(date.getTime() - diff * DAY_MS);
};

export const addDays = (d: Date, n: number): Date => new Date(d.getTime() + n * DAY_MS);

const pad = (n: number) => String(n).padStart(2, "0");

/** Valor para `<input type="date">` (yyyy-mm-dd, calendário local). */
export const toDateInput = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Datas "yyyy-mm-dd..." (API/input date); parse manual evita o deslocamento UTC do Date(string). */
export const parseIsoDate = (value: string): Date => {
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
};

export interface PeriodRange {
  start: Date;
  end: Date;
}

/** Intervalo do período ancorado em `anchor`: semana = seg–dom; mês = mês-calendário. */
export const periodRange = (period: BoardPeriod, anchor: Date): PeriodRange | null => {
  if (period === "all") return null;
  if (period === "week") {
    const start = isoMonday(anchor);
    return { start, end: addDays(start, 6) };
  }
  return {
    start: new Date(anchor.getFullYear(), anchor.getMonth(), 1),
    end: new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0),
  };
};

/**
 * Tarefas que casam com `matches` + seus ancestrais (épico/pai), para os agrupamentos
 * por hierarquia (board/árvore) não quebrarem quando um filtro esconde o pai.
 */
export const keepWithAncestors = (
  tasks: ProjectTask[],
  matches: (t: ProjectTask) => boolean
): ProjectTask[] => {
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const keep = new Set<number>();
  tasks.forEach((t) => {
    if (!matches(t)) return;
    keep.add(t.id);
    let parent = t.parentTaskId ?? null;
    let guard = 0;
    while (parent != null && guard++ < 12) {
      keep.add(parent);
      parent = byId.get(parent)?.parentTaskId ?? null;
    }
  });
  return tasks.filter((t) => keep.has(t.id));
};

/**
 * Tarefas cujo intervalo startDate/dueDate sobrepõe o período. Tarefas sem nenhuma data ficam
 * ocultas. Épicos não casam sozinhos (não viram card no board), mas os ancestrais das tarefas
 * que casam são mantidos para o agrupamento por épico continuar funcionando.
 */
export const filterTasksByPeriod = (tasks: ProjectTask[], range: PeriodRange | null): ProjectTask[] => {
  if (!range) return tasks;
  const matches = (t: ProjectTask) => {
    if (t.type === TaskType.Epic) return false;
    const start = t.startDate ? parseIsoDate(t.startDate) : null;
    const due = t.dueDate ? parseIsoDate(t.dueDate) : null;
    if (!start && !due) return false;
    const from = start ?? due!;
    const to = due ?? start!;
    return from.getTime() <= range.end.getTime() && to.getTime() >= range.start.getTime();
  };
  return keepWithAncestors(tasks, matches);
};
